import React, { useEffect, useState, useRef, useCallback } from 'react';
import { VariableSizeList } from 'react-window';
import { AutoSizer } from 'react-virtualized';
import InfiniteLoader from 'react-window-infinite-loader';

import TimelineItem from 'components/SecondaryView/_components/TimelineItem';

import api from 'services/api';
import { decryptAES_CBC } from 'hooks/useImageDecrypt';
import { useDispatch } from 'react-redux';
import { setSeekVideoShot } from 'store/videoSeektimeSlice';

interface IExternalTimelineView {
  videoHashId: string;
  videoShotId: string;
  topSectionHeight?: number;
  aesKeyId: string;
  encrytedKeys: any;
  currentVideoShot: number;
}

type ItemSizeMap = Map<number, number>;

function ExternalTimelineView({
  videoHashId,
  videoShotId,
  topSectionHeight,
  aesKeyId,
  encrytedKeys,
  currentVideoShot
}: IExternalTimelineView) {
  const [timelines, setTimelines] = useState<any[]>([]);
  const [nextUrl, setNextUrl] = useState<string>('');
  const listRef = useRef<VariableSizeList | null>(null);
  const itemSizeMap = useRef<ItemSizeMap>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const isItemLoaded = (index: number) => index < timelines.length;

  const [decryptedData, setDecryptedData] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      const response = await api.get(`/api/videos/${videoHashId}/shots/${videoShotId}/external-timelines/`);
      const { results } = response.data;
      const nextUrl = response.data?.links?.next;
      setTimelines((prevData: any[]) => {
        const updatedData = [...prevData, ...results];
        return updatedData;
      });
      setNextUrl(nextUrl);
    };

    const decryptKeys = async () => {
      if (aesKeyId && encrytedKeys) {
        const { data, iv } = encrytedKeys;
        const decrypted = await decryptAES_CBC(data, iv);
        setDecryptedData(decrypted);
      }
    };

    if (videoShotId && videoHashId) {
      fetchData();
      decryptKeys();
    }

    return () => {
      setTimelines([]);
      setNextUrl('');
    };
  }, [videoShotId, videoShotId]);

  const getItemSize = (index: number) => {
    return itemSizeMap.current.get(index) || 50; // default size of 50, item will be resized dynamically
  };

  const setItemSize = (index: number, size: number) => {
    const currentSize = itemSizeMap.current.get(index);
    if (currentSize !== size && listRef.current) {
      // Ensure listRef.current is not null
      itemSizeMap.current.set(index, size + 4); // add 4px for height to make room
      // Optionally, you can force the list to recompute its layout without triggering a re-render
      // This prevents the scrolling behavior from resetting to the top
      listRef.current.resetAfterIndex(index, true);
    }
  };

  const loadMoreItems = () => {
    if (!loading && nextUrl) {
      fetchNextUrl(nextUrl);
    }
  };

  const fetchNextUrl = (url: string) => {
    setLoading(true);
    api.get(url).then((res) => {
      const { results } = res.data;
      const next = res.data?.links?.next;
      setTimelines((prevData: any[]) => {
        const updatedData = [...prevData, ...results];
        return updatedData;
      });
      setNextUrl(next);
      setLoading(false);
    });
  };

  const ItemRenderer = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const timeline = timelines[index];
    let displayAvatar = true;

    if (index > 0 && timeline?.user_info) {
      const prevTimeline = timelines[index - 1];
      if (prevTimeline && prevTimeline.user_info) {
        displayAvatar = prevTimeline.user_info.hash_id !== timeline.user_info.hash_id;
      }
    }

    const itemRef = useCallback(
      (node) => {
        if (node !== null) {
          const height = node.getBoundingClientRect().height;
          const currentSize = itemSizeMap.current.get(index);

          // Only update if the size has changed to prevent unnecessary renders
          if ((height && !currentSize) || currentSize !== height) {
            setItemSize(index, height);
          }
        }
      },
      [index]
    );

    if (!timeline) {
      return (
        <div style={{ ...style, height: '40px' }} className="w-full bg-white flex justify-center">
          Loading...
        </div>
      );
    }

    const seekPlayer = (time: number) => {
      dispatch(setSeekVideoShot({ time: time, videoShotIdx: currentVideoShot }));
    };

    return (
      <div style={{ ...style }} className="relative hover:bg-[#f8f8f8]">
        <div ref={itemRef} className="py-1">
          <TimelineItem
            timeline={timeline}
            displayAvatar={displayAvatar}
            navigateToVideo={seekPlayer}
            user={{
              avatar: timeline.user_info?.avatar ? timeline.user_info.avatar : timeline.active_speaker_thumbnail_url,
              display_name: timeline.user_info?.display_name
                ? timeline.user_info?.display_name
                : timeline.user_info?.name || 'Unknown',
              email: timeline.user_info?.email,
              name: timeline.user_info?.name,
              id: timeline.user_info?.user_id
            }}
            videoHashId={videoHashId}
            aesKeyId={aesKeyId}
            decryptionKeys={decryptedData}
            isEditing={false}
            videoShotId={videoShotId}
            isExternal={true}
            handleEdit={noop}
            handleSpeaker={noop}
            handleCancelEdit={noop}
            onDelete={noop}
          ></TimelineItem>
        </div>
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};

  return (
    <div
      className={`${
        topSectionHeight ? '' : 'border rounded-md'
      } mr-3 border-line h-full w-full md:w-96 external-timeline-container`}
    >
      <div>
        <p
          className={`mb-2 border-b ${
            topSectionHeight ? 'text-xl font-semibold pt-0 pb-4 px-4' : 'font-bold text-base p-2'
          }`}
        >
          Timeline
        </p>
      </div>
      <div
        className="pt-2 flex flex-col overflow-y-auto"
        style={{
          height: topSectionHeight ? `calc(100vh - ${topSectionHeight + 100}px)` : `calc(100vh - 150px)`
        }}
      >
        <AutoSizer>
          {({ height, width }: any) => (
            <div className="relative">
              <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={timelines.length + (nextUrl ? 1 : 0)}
                loadMoreItems={loadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <VariableSizeList
                    height={height}
                    itemSize={getItemSize}
                    itemCount={timelines.length + (nextUrl ? 1 : 0)}
                    onItemsRendered={onItemsRendered}
                    ref={(list) => {
                      // Assigning the ref to the provided ref and calling resetAfterIndex when necessary
                      ref(list);
                      if (list) {
                        listRef.current = list;
                      }
                    }}
                    width={width}
                  >
                    {ItemRenderer}
                  </VariableSizeList>
                )}
              </InfiniteLoader>
            </div>
          )}
        </AutoSizer>
      </div>
    </div>
  );
}

export default ExternalTimelineView;
